/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { useErrorState } from '../../shared/useErrorState';
import { VStack, Image } from 'chakra-v3';
import type { PlatformImageComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { PlatformMediaErrors } from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';
import { ChatBubble } from '../../shared/ChatBubble';

export const PlatformImage: React.FC<ComponentRendererProps> = ({ node, controller, isCurrent }) => {
  const params = getTypedParams<PlatformImageComponent>(node);
  const image = params.image;
  const [error, setError] = useErrorState();

  // Handle next component navigation - automatically advance, image can be lazy loaded
  useNext({
    strategy: 'AUTO',
    controller,
    isCurrent,
  });

  const handleImageError = () => {
    const error = PlatformMediaErrors.getMediaLoadError('image', image || undefined);
    setError(() => error);
  };

  return (
    <ChatBubble>
      <VStack gap="12px" align="stretch">
        {error && (
          <Text variant="body" color="error.solid">
            {error.message}
          </Text>
        )}
        <Image
          src={image}
          alt="Platform content"
          maxH="500px"
          w="auto"
          mx="auto"
          borderRadius="4px"
          onError={handleImageError}
        />
      </VStack>
    </ChatBubble>
  );
};
